<script setup>
import {computed, reactive, inject, ref, onMounted, watch, provide} from "vue";
//import functions
import {required} from "@vuelidate/validators";
import {discordRegistrationTime} from "@/utils/discordRegistrationTime.js";
import {formatPrice} from "@/utils/priceFormatter.js";
import {cleanObject} from "@/utils/cleanObject.js";
//import components
import {Icon} from "@iconify/vue";
import {
  Dialog,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  InputGroup,
  InputGroupAddon,
  InputText,
  FloatLabel,
  Select,
  Textarea,
  InputNumber,
  Tag
} from "primevue";
import Attachments from "@/views/orders/Details/Attachments.vue";
import Users from "@/components/Users.vue";
import UserProfile from "@/components/UserProfile/index.vue";
// import PublishHotOrder from "@/views/orders/Details/hotOrders/PublishHotOrder.vue";
//import stores
import {useUserStore} from "@/stores/user.js";
import {useCurrencyPricesStore} from "@/stores/currencyPrices.js";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import {useConfigStore} from "@/stores/config.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useVuelidate} from "@vuelidate/core";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {useRouter} from "vue-router";
import {showMessage} from "@/utils/showMessage.js";

//stores
const userStore = useUserStore();
const currencyPricesStore = useCurrencyPricesStore();
const ordersStore = useMainOrdersStore()
const configStore = useConfigStore();

//loading flags
const {
  saving,
  fetching,
  setSaving,
  setFetching,
} = useLoadingFlags()
const isUploadingAttachment = ref(false)
const isShowUserProfileModal = ref(false);
const isShowPublishModal = ref(false);
const hotOrderID = ref(0)
const savingBeforePosting = ref(false)

//other composables
const {isXL} = useBreakpoint();
const toast = useToast();
const router = useRouter()

//emits
const emit = defineEmits(['refresh', 'refreshOrder']);

//props
const props = defineProps({
  addOrder: {
    type: Function,
    required: false
  },
  orderType: {
    type: String,
    required: true
  },
  order: {
    type: Object,
    required: true
  }
})

//injects
const visible = inject('isShowAddHotOrderModal')

//component variables
const amount = ref(null)
const profitMargin = ref(null)
const selectedTaker = ref({})
const selectedCurrency = ref({})
const serverUsers = ref([])
const currencyPrices = ref([])
const activeTab = ref("0")
const hotOrderAmountNum = computed(() => Number(hotOrder.amount))
const amountNum = computed(() => Number(amount.value))
const rateNum = computed(() => Number(hotOrder.rate))
const valueNum = computed(() => Number(hotOrder.value))
const profitNum = computed(() => Number(profitMargin.value))
const excludeCurrencyPrices = ["Toman Gold Buy Manual", "Toman Gold Buy Auto", "Toman Shop"];

const hasProfitField = computed(() => userStore.hasPermission('BIG_BOSS') && props.orderType === 'Account')
const canShowUserProfile = computed(() => userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('ADMIN'))

const defaultTitle = computed(() => {
  const games = ['FC 25', 'FC 26', 'GTA Online', 'Call of Duty', 'Apex Legends', 'Fortnite', 'Marvel Rivals', 'Valorant']
  if (props.orderType === 'Account' && games.includes(props.order.game)) {
    return props.order.title
  }
})

// Funpay community check (by takerID === 11)
const isFunpayCommunity = computed(() => hotOrder.takerID === 11)

// NEW: Preview computations
const oldContribution = 0;
const newContribution = computed(() => {
  return hotOrder.state !== 'Canceled' ? valueNum.value : 0;
});
const newTakersCut = computed(() => {
  return Number(props.order.takersCut || 0) + newContribution.value;
});
const newRevenue = computed(() => {
  const income = Number(props.order.income || 0);
  return income - newTakersCut.value;
});

const states = ['New', 'Assigned', 'Delivering']
const stateColorsMap = {
  New: 'bg-[#9E9E9E]',
  Assigning: 'bg-[#EFECDC]',
  Assigned: 'bg-[#FFEB01]',
  Delivering: 'bg-[#FF9800]',
  Delivered: 'bg-[#2196F3]',
  Canceled: 'bg-[#F44336]',
  Paid: 'bg-[#4CAF50]',
}

const attachments = ref(null)
const hotOrder = reactive({
  "orderID": props.order?.id ?? null,
  "state": "New",
  "title": defaultTitle.value || "",
  "rate": "",
  "amount": "",
  "value": "",
  "currency": "",
  "takerID": null,
  "description": "",
  "notes": "",
  "attachments": {
    "paymentReceipts": []
  }
})
const rules = computed(() => {
  return {
    state: {required},
    title: {required},
    rate: {required},
    amount: {required},
    value: {required},
    currency: {required},
    takerID: !selectedTaker.value?.id && hotOrder.state !== 'New' ? {required} : {}
  };
});
const $vuelidate = useVuelidate(rules, hotOrder);
const sortedTags = computed(() => {
  if (!selectedTaker.value?.tags) return [];
  return Object.entries(selectedTaker.value.tags).sort((a, b) => b[1] - a[1]); // descending by value
});

//component functions
function formatNumber(num, decimals = 8) {
  return num.toFixed(decimals).replace(/\.?0+$/, '');
}

const onSave = async (isPublishing) => {
  $vuelidate.value.$touch();
  if ($vuelidate.value.$invalid) {
    activeTab.value = "0"
    return false;
  }

  if (isUploadingAttachment.value) {
    activeTab.value = "1"
    return false;
  }

  if (selectedTaker.value?.id && hotOrder.state === 'New') {
    showMessage('Order state must be updated to assign a taker.', 'warning', 2000, false, 'top-center', 'text-slate-700 font-semibold');
    return false
  }

  if (!selectedTaker.value?.id && hotOrder.state !== 'New') {
    console.log('it does not have taker')
  } else {
    if (isPublishing) {
      savingBeforePosting.value = true
    } else {
      setSaving(true)
    }
    if (props.order.id) {
      try {
        await saveHotOrder(props.order.id, isPublishing)
      } catch (err) {
        showMessage(err, 'error');
      } finally {
        setSaving(false)
      }
    } else {
      try {
        const order = cleanObject({
          ...props.order,
          revenue: formatNumber(newRevenue.value)
        })
        const orderID = await props.addOrder(order)
        if (orderID) {
          await saveHotOrder(orderID, isPublishing)
        }
      } catch (err) {
        showMessage(err, 'error');
      } finally {
        if (isPublishing) {
          savingBeforePosting.value = false
        } else {
          setSaving(false)
        }
      }
    }
  }
}

const saveHotOrder = async (orderID, isPublishing) => {
  try {
    if (attachments.value.length) {
      hotOrder.attachments = {paymentReceipts: attachments.value}
    }
    const res = await ordersStore.action_addHotOrder(props.orderType, {...hotOrder, "orderID": orderID})
    if (res.result === true) {
      hotOrderID.value = res.data.id
      showMessage(`Sub Order Added!`, 'success');
    } else {
      showMessage(res, 'error');
    }
    if (!isPublishing) {
      if (props.order.id) {
        emit('refreshOrder')
      } else {
        orderDetails(orderID);
      }
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    if (!isPublishing) {
      visible.value = false
    }
  }
}

function orderDetails(orderID) {
  const routeName = {
    Account: 'account-order-details',
    'Gift Card': 'gift-card-order-details',
    Item: 'item-order-details',
    Boost: 'boost-order-details'
  }
  const route = {name: routeName[props.orderType], params: {id: orderID}}
  router.replace(route)
}

const onStateChange = () => {
  if (hotOrder.state === 'Canceled') {
    hotOrder.value = "0"
  } else {
    if (hotOrder.currency === 'Toman') {
      hotOrder.value = formatNumber((hotOrderAmountNum.value * 1000) / rateNum.value, 2)
    } else {
      hotOrder.value = formatNumber(hotOrderAmountNum.value * rateNum.value, 2)
    }
  }
};
const onPriceInput = (e) => {
  hotOrder.rate = e.value
  if (hotOrder.state === 'Canceled') {
    hotOrder.value = "0"
    return
  }
  calc();
};
const onAmountInput = (e) => {
  amount.value = e.value
  if (hotOrder.state === 'Canceled') {
    hotOrder.value = "0"
    return
  }
  calc()
};
const onProfitInput = () => {
  // profitMargin is used when we only have one hot order and is only used by BIG_BOSS.
  const profit = profitMargin.value || 0
  const priceWithProfit = Number(props.order.price * (profit / 100))
  const incomeNum = Number(props.order.income)
  hotOrder.amount = formatNumber(incomeNum - priceWithProfit, 2)
  amount.value = formatNumber(incomeNum - priceWithProfit, 2)
  hotOrder.value = formatNumber(hotOrderAmountNum.value * rateNum.value, 2)
}

const onChangeCurrency = (currency) => {
  hotOrder.currency = currency.name
  hotOrder.rate = currency.price
  calc();
}

const getSelectedTaker = (taker) => {
  if (selectedTaker.value?.id && hotOrder.state === 'New') {
    hotOrder.state = 'Assigned'
  }

  selectedTaker.value = taker || null
  hotOrder.takerID = taker ? taker.id : null
  calc()
}

const getCurrencies = async (refresh) => {
  try {
    if (!refresh) setFetching(true)
    const res = await currencyPricesStore.action_getPrices();
    if (res.result === true) {
      currencyPrices.value = res.data;

      //set initial data
      selectedCurrency.value = currencyPrices.value.find(item => item.name === 'USDT')
      hotOrder.currency = selectedCurrency.value.name
      hotOrder.rate = selectedCurrency.value.price
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 1500});
      showMessage(res, 'error');
    }
  } catch (err) {
    //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
    showMessage(err, 'error');
  } finally {
    setFetching(false)
  }
}

const calc = () => {
  const currency = hotOrder.currency

  if (isFunpayCommunity.value) {
    hotOrder.amount = formatNumber(amountNum.value * 1.03, 2)
  } else {
    hotOrder.amount = amount.value
  }

  if (currency === 'Toman') {
    if (rateNum.value) {
      hotOrder.value = formatNumber((hotOrderAmountNum.value * 1000) / rateNum.value, 2)
    } else {
      hotOrder.value = "0"
    }
  } else {
    hotOrder.value = formatNumber(hotOrderAmountNum.value * rateNum.value, 2)
  }
}

function showUserProfileModal(user) {
  if (user.isLoading) return;
  if (canShowUserProfile.value) {
    userStore.setSelectedUser(user);
    isShowUserProfileModal.value = true
  }
}

const showPublishModal = async () => {
  await onSave(true);
  isShowPublishModal.value = true
}

//hooks
onMounted(() => {
  getCurrencies(false)
})

//provides
provide('isShowUserProfileModal', isShowUserProfileModal)
provide('isShowPublishModal', isShowPublishModal)
</script>

<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="min-w-full min-h-full flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)] !pb-3 lg:!pb-5 !px-3 lg:!px-5">
    <template #header>
      <div class="text-base py-3">
        New Sub Order
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm lg:min-w-80 max-w-full">
      <Tabs v-model:value="activeTab" class="custom-tabs">
        <div class="grid grid-cols-12 lg:flex flex-row gap-4 lg:gap-2 justify-between items-center border-b border-slate-700 pb-2">
          <TabList class="col-span-12">
            <Tab value="0">Details</Tab>
            <Tab value="1">Attachments</Tab>
          </TabList>
          <div class="col-span-12 flex flex-col gap-0.5">
            <FloatLabel class="w-full custom-select"
                        :class="{ 'has-error': $vuelidate.state.$error}"
                        variant="on">
              <Select v-model="hotOrder.state" inputId="state" :options="states"
                      class="w-full min-w-28 !bg-transparent h-[28px] 2xl:h-[32px] !text-xs 2xl:!text-sm"
                      :class="[$vuelidate.state.$error ? '!border !border-red-300' : '!border-slate-700']"
                      input-class="!py-1 !items-center !flex !pr-0"
                      overlay-class="select-overlay-in-modal h-max"
                      @change="onStateChange">
                <template #value="slotProps">
                  <div class="flex items-center gap-2 !text-xs">
                    <span class="w-2 h-2 rounded-full" :class="stateColorsMap[slotProps.value]"></span>
                    {{ slotProps.value }}
                  </div>
                </template>
                <template #option="slotProps">
                  <div class="flex items-center gap-2 !text-xs 2xl:!text-sm">
                    <span class="w-2 h-2 rounded-full" :class="stateColorsMap[slotProps.option]"></span>
                    {{ slotProps.option }}
                  </div>
                </template>
              </Select>
            </FloatLabel>
            <template v-if="$vuelidate.state.$error">
              <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
                 v-if="!$vuelidate.state.required.$response">
                This field is required
              </p>
            </template>
          </div>
        </div>
        <TabPanels>
          <TabPanel value="0">
            <div class="grid grid-cols-12 gap-4">
              <div class="col-span-12">
                <div class="bg-slate-800 rounded-md p-3 border border-slate-700">
                  <h4 class="text-sm font-medium text-slate-300 mb-2">Preview Changes</h4>
                  <div class="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <p class="text-slate-400">New Takers Cut</p>
                      <p class="text-white font-mono">${{ formatPrice(formatNumber(newTakersCut, 2)) }}</p>
                    </div>
                    <div>
                      <p class="text-slate-400">New Revenue</p>
                      <p class="text-white font-mono">${{ formatPrice(formatNumber(newRevenue, 2)) }}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-span-12 lg:col-span-8 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                <FloatLabel variant="on">
                  <InputText id="hotOrderTitle" v-model="hotOrder.title" autocomplete="off"
                             class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                             :class="{ 'has-error': $vuelidate.title.$error }"/>
                  <label for="hotOrderTitle" class="!bg-slate-900"
                         :class="{ 'focus:text-red-300': $vuelidate.title.$error }">Title</label>
                </FloatLabel>
                <template v-if="$vuelidate.title.$error">
                  <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
                     v-if="!$vuelidate.title.required.$response">
                    This field is required
                  </p>
                </template>
              </div>
              <div class="col-span-12 lg:col-span-4 flex flex-row gap-2 text-xs 2xl:text-sm">
                <div class="flex-1 w-full">
                  <FloatLabel variant="on" :class="{ 'has-error': $vuelidate.currency.$error }">
                    <Select v-model="selectedCurrency" input-id="currency"
                            :options="currencyPrices.filter(item => !excludeCurrencyPrices.includes(item.name))"
                            optionLabel="name" :loading="fetching"
                            class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                            :class="{ 'has-error has-addon': $vuelidate.currency.$error }"
                            label-class="!text-sm !items-center !flex"
                            @update:modelValue="onChangeCurrency">
                      <template #option="slotProps">
                        <div class="flex flex-row items-center gap-4 justify-between w-full">
                          <span class="text-sm">{{ slotProps.option.name }}</span>
                          <div class="flex flex-row gap-2 items-center">
                            <span class="text-xs">
                              {{ slotProps.option.price }} {{ slotProps.option.name === 'Toman' ? 'Tmn' : '$' }}
                            </span>
                            <div class="h-4 w-[1px] bg-slate-500"></div>
                            <span class="text-[.7rem]">
                              {{ gregorianDate(slotProps.option.lastUpdate, 'DD MMM HH:mm') }}
                            </span>
                          </div>
                        </div>
                      </template>
                    </Select>
                    <label for="currency" class="!bg-slate-900"
                           :class="{ 'has-error': $vuelidate.currency.$error }">Currency</label>
                  </FloatLabel>
                </div>
                <button @click="getCurrencies(false)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                       class="lucide lucide-refresh-cw-icon lucide-refresh-cw">
                    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                    <path d="M21 3v5h-5"/>
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                    <path d="M8 16H3v5"/>
                  </svg>
                </button>
              </div>
              <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm w-full"
                   :class="[hasProfitField ? 'lg:col-span-3' : 'lg:col-span-4']">
                <InputGroup class="w-full">
                  <FloatLabel variant="on"
                              :class="{ 'has-error': $vuelidate.amount.$error }">
                    <InputNumber id="amount" v-model="amount"
                                 autocomplete="off" :maxFractionDigits="2"
                                 :input-class="['!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700',
                                     { 'has-error has-addon': $vuelidate.amount.$error }
                                   ]"
                                 @input="onAmountInput"/>
                    <label for="amount" class="!bg-slate-900"
                           :class="{ 'has-error': $vuelidate.amount.$error }">Amount</label>
                  </FloatLabel>
                  <InputGroupAddon
                      class="!bg-transparent !border-slate-700 h-[32px] 2xl:h-[40px] text-[.7rem] whitespace-nowrap w-auto flex-shrink-0 !px-1">
                    {{ hotOrder.currency === 'Toman' ? '1000 Tmn' : hotOrder.currency }}
                  </InputGroupAddon>
                </InputGroup>
                <!-- Effective Amount display for funpay -->
                <p v-if="isFunpayCommunity && hotOrder.amount && amount && hotOrder.state !== 'Canceled'"
                   class="text-yellow-400 text-xs mt-1">
                  Effective Amount: ${{ hotOrder.amount }}
                </p>
                <template v-if="$vuelidate.amount.$error">
                  <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
                     v-if="!$vuelidate.amount.required.$response">
                    This field is required
                  </p>
                </template>
              </div>
              <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm"
                   :class="[hasProfitField ? 'lg:col-span-3' : 'lg:col-span-4']">
                <InputGroup>
                  <FloatLabel variant="on"
                              :class="{ 'has-error': $vuelidate.rate.$error }">
                    <InputNumber id="hotOrderPrice" v-model="hotOrder.rate"
                                 autocomplete="off" :max-fraction-digits="8"
                                 input-class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                 :input-class="{ 'has-error has-addon': $vuelidate.rate.$error }"
                                 @input="onPriceInput"/>
                    <label for="hotOrderPrice" class="!bg-slate-900"
                           :class="{ 'has-error': $vuelidate.rate.$error }">Rate</label>
                  </FloatLabel>
                  <InputGroupAddon class="!bg-transparent !border-slate-700 h-[32px] 2xl:h-[40px] text-xs">
                    {{ hotOrder.currency === 'Toman' ? 'Tmn' : '$' }}
                  </InputGroupAddon>
                </InputGroup>
                <template v-if="$vuelidate.rate.$error">
                  <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
                     v-if="!$vuelidate.rate.required.$response">
                    This field is required
                  </p>
                </template>
              </div>
              <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm"
                   :class="[hasProfitField ? 'lg:col-span-3' : 'lg:col-span-4']">
                <InputGroup>
                  <FloatLabel variant="on"
                              :class="{ 'has-error': $vuelidate.value.$error }">
                    <InputNumber id="value" v-model="hotOrder.value"
                                 autocomplete="off" readonly disabled :maxFractionDigits="2"
                                 input-class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                 :input-class="{ 'has-error has-addon': $vuelidate.value.$error, 'p-filled' : hotOrder.value }"/>
                    <label for="value" class="!bg-slate-900"
                           :class="{ 'has-error': $vuelidate.value.$error }">Value</label>
                  </FloatLabel>
                  <InputGroupAddon class="!bg-transparent !border-slate-700 h-[32px] 2xl:h-[40px] text-xs">$</InputGroupAddon>
                </InputGroup>
                <template v-if="$vuelidate.value.$error">
                  <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
                     v-if="!$vuelidate.value.required.$response">
                    This field is required
                  </p>
                </template>
              </div>
              <div v-if="hasProfitField"
                   class="col-span-12 lg:col-span-3 flex flex-col gap-0.5">
                <InputGroup>
                  <FloatLabel variant="on">
                    <InputText v-allow-digits-dot id="profitMargin" v-model="profitMargin"
                               autocomplete="off"
                               class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                               @input="onProfitInput"/>
                    <label for="profitMargin" class="!bg-slate-900">Profit Margin</label>
                  </FloatLabel>
                  <InputGroupAddon class="!bg-transparent !border-slate-700 h-[32px] 2xl:h-[40px]">%</InputGroupAddon>
                </InputGroup>
              </div>
              <div class="col-span-12 flex flex-row gap-2 text-xs 2xl:text-sm">
                <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full">
                  <Users v-model="selectedTaker" @update:modelValue="getSelectedTaker" appendTo="body" label="Taker"
                         :required="!selectedTaker?.id && hotOrder.state !== 'New'"/>
                </div>
              </div>
              <div v-if="selectedTaker?.id"
                   class="col-span-12 flex flex-col w-full gap-2 border border-slate-700 rounded-md p-2">
                <div class="flex flex-row gap-2 w-full items-center">
                  <div @click.stop="showUserProfileModal(selectedTaker)"
                       :class="[canShowUserProfile ? 'cursor-zoom-in' : 'cursor-default']">
                    <img v-if="selectedTaker.discordAvatar" :src="selectedTaker.discordAvatar"
                         alt="avatar" class="size-10 rounded-full border border-slate-700">
                    <div v-else
                         class="flex items-center justify-center size-10 rounded-full border border-slate-700">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                           fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                           stroke-linejoin="round" class="lucide lucide-user-icon lucide-user">
                        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                    </div>
                  </div>
                  <div class="flex-1 grid grid-cols-2 gap-1">
                    <div class="flex flex-col">
                      <div class="flex flex-row gap-2 items-center">
                        <div>{{ selectedTaker.name }} &nbsp &nbsp</div>
                        <div v-if="selectedTaker.rating" class="flex flex-row items-center gap-0.5 text-xs">
                          <span class="flex flex-row items-end">
                            {{ selectedTaker.rating }}⭐ ({{ selectedTaker.ratingCount }})
                          </span>
                        </div>
                      </div>
                      <div class="flex flex-row w-max text-xs text-slate-400">
                        <div>{{ selectedTaker.discordUsername ?? "-" }}</div>
                        <div>{{ "&nbsp- " + selectedTaker.discordDisplayName ?? "-" }}</div>
                      </div>
                    </div>
                    <div class="flex flex-col">
                      <div>{{ selectedTaker.verificationState ?? "-" }}</div>
                      <div>{{ discordRegistrationTime(selectedTaker.discordID) }}</div>
                    </div>
                  </div>
                </div>
                <div v-if="selectedTaker?.tags && Object.entries(selectedTaker.tags).length > 0"
                     class="flex flex-wrap gap-1">
                  <Tag v-for="[key, value] in sortedTags"
                       :severity="configStore.tags[key] === 1 ? 'success' : 'danger'"
                       :value="`${key} (${value})`" class="!font-normal !text-xs"></Tag>
                </div>
              </div>
              <div class="col-span-12 grid grid-cols-2 gap-4">
                <div class="col-span-2 lg:col-span-1 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                  <FloatLabel variant="on">
                    <Textarea v-auto-dir id="description" v-model="hotOrder.description" rows="5"
                              style="resize: none"
                              class="!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700"/>
                    <label for="description" class="!bg-slate-900">Description</label>
                  </FloatLabel>
                </div>
                <div class="col-span-2 lg:col-span-1 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                  <FloatLabel variant="on">
                    <Textarea v-auto-dir id="notes" v-model="hotOrder.notes" rows="5" style="resize: none"
                              class="!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700"/>
                    <label for="notes" class="!bg-slate-900">Notes</label>
                  </FloatLabel>
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel value="1">
            <Attachments
                id="hotOrder"
                :count="16"
                :only-changes="true"
                size-class="w-24 h-24"
                font-size="text-[.6rem]"
                v-model:uploading="isUploadingAttachment"
                @update:payload="val => (attachments = val)"
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
    <div class="flex justify-end gap-3">
      <!--      <button v-if="orderType === 'Boost' && order.id && hotOrder.state === 'New'" @click.stop="showPublishModal"
                    class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1"
                    :disabled="savingBeforePosting">
              Save & Publish
              <Icon v-if="savingBeforePosting" icon="eos-icons:bubble-loading" height="24"/>
            </button>-->
      <button @click="onSave(false)"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1"
              :disabled="saving">
        Save
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
      </button>
    </div>
  </Dialog>
  <UserProfile v-if="isShowUserProfileModal" :userID="userStore.selectedUser.id"/>
  <!--  <PublishHotOrder v-if="isShowPublishModal" :orderType="orderType" :subOrder="hotOrder" :hotOrderID="hotOrderID"/>-->
</template>
<style></style>